import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faYoutube,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

/** Reusable collapsible section for mobile */
function FooterSection({ id, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section aria-labelledby={id} className="border-white/5 md:border-0">
      {/* Header button (collapsible on mobile) */}
      <button
        type="button"
        className="w-full flex items-center justify-between py-3 md:py-0 md:cursor-auto"
        aria-controls={`${id}-panel`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <h3
          id={id}
          className="text-base md:text-lg font-semibold tracking-tight text-white"
        >
          {title}
        </h3>
        <span className="md:hidden ml-4 inline-flex h-6 w-6 items-center justify-center rounded-md ring-1 ring-white/15 text-slate-200">
          <svg
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-45" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            {/* plus icon becomes × when rotated */}
            <path d="M9 3h2v14H9zM3 11V9h14v2H3z" />
          </svg>
        </span>
      </button>

      {/* Content: collapsible on mobile, always visible on md+ */}
      <div
        id={`${id}-panel`}
        className={`overflow-hidden transition-[max-height] duration-300 md:transition-none md:max-h-none ${
          open ? "max-h-[1000px] pt-1" : "max-h-0 md:max-h-none md:pt-0"
        }`}
      >
        <div className="md:mt-4 text-[15px]">{children}</div>
      </div>
    </section>
  );
}

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative text-slate-100 bg-gradient-to-b from-[#0f172a] via-[#1e293b] to-[#0b1220]">
      {/* Accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500" />

      {/* Main content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Grid switches to accordion on mobile */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {/* Get to Know Us */}
          <FooterSection id="footer-know-us" title="Get to Know Us" defaultOpen>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    About Us
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/programs"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Programs
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/event"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Events
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Gallery
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/review"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Review
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Contact
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/booking"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Book Now
                  </span>
                </Link>
              </li>
            </ul>
          </FooterSection>

          {/* Social */}
          <FooterSection id="footer-connect" title="Connect with Us" defaultOpen>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.facebook.com/buildpro.ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-10 w-10 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon icon={faFacebook} className="text-[#1877F2]" aria-hidden="true" />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Facebook
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://x.com/BuildproOGCS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-10 w-10 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon icon={faTwitter} className="text-[#1DA1F2]" aria-hidden="true" />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Twitter
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/buildpro.ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-10 w-10 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon icon={faInstagram} className="text-[#E4405F]" aria-hidden="true" />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Instagram
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/@OGCS_01"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-10 w-10 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon icon={faYoutube} className="text-[#FF0000]" aria-hidden="true" />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    YouTube
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/buildpro-ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-10 w-10 sm:h-9 sm:w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon icon={faLinkedin} className="text-[#0077B5]" aria-hidden="true" />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    LinkedIn
                  </span>
                </a>
              </li>
            </ul>
          </FooterSection>

          {/* Contact */}
          <FooterSection id="footer-contact" title="Contact Us" defaultOpen>
            <address className="not-italic text-slate-300 leading-7 text-[15px]">
              <strong className="block text-slate-100">OGCS Private Limited</strong>
              <span className="block">No 03, C-6, 14, Rd Number 2</span>
              <span className="block">Sector 18, New Panvel, Dist: Raigad</span>
              <span className="block">Pin Code: 410206</span>

              <span className="block mt-3">
                <span className="font-medium text-slate-200">Mobile:</span>{" "}
                <a
                  href="tel:+919967610135"
                  className="text-sky-300 hover:text-white underline underline-offset-4 break-all"
                >
                  +91 9152949738
                </a>
              </span>

              <span className="block">
                <span className="font-medium text-slate-200">Email:</span>{" "}
                <a
                  href="mailto:og.constructionsolution@gmail.com"
                  className="text-sky-300 hover:text-white underline underline-offset-4 break-all"
                >
                  og.constructionsolution@gmail.com
                </a>
              </span>
            </address>
          </FooterSection>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-slate-400 text-center md:text-left">
            © {year} Growmax OGCS. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/terms-and-conditions"
              className="text-sky-300 hover:text-white underline underline-offset-4"
            >
              Terms &amp; Conditions
            </Link>
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="text-slate-400 hover:text-white transition"
            >
              Back to top ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
